import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import {
  ArrowLeft, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Maximize, Minimize,
  Subtitles, AudioLines, Gauge, MonitorPlay, PictureInPicture2, RotateCw,
} from "lucide-react";

// In-browser player. Picks the cheapest way to get each file onto the screen:
//   direct    - original file via /stream (Range), zero server work
//   remux     - video copied, audio -> AAC (server, nearly free)     [/webplay/stream?mode=remux]
//   transcode - video -> H.264 720p max + AAC (server, CPU heavy)    [/webplay/stream?mode=transcode]
// remux/transcode are fragmented-MP4 pipes with no duration/seek table, so the timeline is
// "virtual": position = streamOffset + video.currentTime, duration comes from ffprobe, and a seek
// outside the buffered range restarts the stream at the target time.
// Anything the browser can't do (Dolby Vision, unsupported HDR, busy server, decode errors) falls
// back to the Kodi hand-off via onKodi.

const AUDIO_OK = ["aac", "mp3", "opus", "vorbis", "flac"];
const CONTAINER_OK = ["mov", "matroska", "webm"]; // mp4 family shows up as "mov" in ffprobe
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function detectCaps() {
  const v = document.createElement("video");
  const ok = (t) => { const r = v.canPlayType(t); return r === "probably" || r === "maybe"; };
  const ua = navigator.userAgent;
  const safari = /^((?!chrome|android).)*safari/i.test(ua);
  return {
    hevc: ok('video/mp4; codecs="hvc1.1.6.L120.B0"') || ok('video/mp4; codecs="hev1.1.6.L120.B0"'),
    av1: ok('video/mp4; codecs="av01.0.05M.08"'),
    vp9: ok('video/webm; codecs="vp9"'),
    ac3: ok('audio/mp4; codecs="ac-3"'),
    eac3: ok('audio/mp4; codecs="ec-3"'),
    mkv: !safari,   // Chromium/Firefox demux Matroska natively; Safari does not
  };
}

// -> { mode: "direct" | "remux" | "transcode" | "kodi", reason }
export function decidePlayback(info, caps, audioIdx = 0) {
  const v = info && info.video;
  if (!v) return { mode: "kodi", reason: "No video stream found" };
  if (v.dolby_vision) return { mode: "kodi", reason: "Dolby Vision needs Kodi" };
  const vc = v.codec;
  const tenBit = /10|12/.test(v.pix_fmt || "");
  let videoOk = false;
  if (vc === "h264") videoOk = !tenBit;
  else if (vc === "hevc") videoOk = caps.hevc;
  else if (vc === "av1") videoOk = caps.av1;
  else if (vc === "vp9") videoOk = caps.vp9;
  else if (vc === "vp8") videoOk = true;
  const a = (info.audio || [])[audioIdx];
  const ac = a && a.codec;
  const audioOk = !a || AUDIO_OK.includes(ac) || (ac === "ac3" && caps.ac3) || (ac === "eac3" && caps.eac3);
  const containerOk = CONTAINER_OK.includes(info.container) && (info.container !== "matroska" || caps.mkv);
  if (videoOk && audioOk && containerOk) return { mode: "direct" };
  if (videoOk) return { mode: "remux" };
  if (v.hdr) return { mode: "kodi", reason: "HDR video this browser can't decode - Kodi handles it" };
  const pixels = (v.width || 0) * (v.height || 0);
  if (vc !== "h264" && pixels > 1280 * 720 * 1.3) return { mode: "kodi", reason: "Large HEVC/AV1 video would be too heavy to convert live" };
  return { mode: "transcode" };
}

function fmtTime(s) {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return (h ? `${h}:${String(m).padStart(2, "0")}` : `${m}`) + `:${String(sec).padStart(2, "0")}`;
}

function parseVtt(text) {
  const cues = [];
  const toSec = (t) => {
    const p = t.trim().replace(",", ".").split(":").map(parseFloat);
    return p.length === 3 ? p[0] * 3600 + p[1] * 60 + p[2] : p[0] * 60 + p[1];
  };
  for (const block of text.replace(/\r/g, "").split(/\n\n+/)) {
    const lines = block.split("\n");
    const i = lines.findIndex((l) => l.includes("-->"));
    if (i < 0) continue;
    const [a, b] = lines[i].split("-->");
    const start = toSec(a), end = toSec(b.trim().split(/\s+/)[0]);
    const body = lines.slice(i + 1).join("\n").replace(/<[^>]+>/g, "").replace(/\{\\[^}]*\}/g, "").trim();
    if (body && isFinite(start) && isFinite(end)) cues.push({ start, end, text: body });
  }
  return cues;
}

export default function WebPlayer({ kind = "movie", id, title, subtitle, next, onKodi }) {
  const nav = useNavigate();
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const offsetRef = useRef(0);          // stream start offset (seconds) for remux/transcode
  const lastSave = useRef(0);
  const progRef = useRef({ pos: 0, dur: 0 });   // latest position, readable after the <video> is gone
  const hideTimer = useRef(null);
  const token = localStorage.getItem("kino_token") || "";

  const [info, setInfo] = useState(null);
  const [caps] = useState(detectCaps);
  const [mode, setMode] = useState(null);
  const [src, setSrc] = useState("");
  const [audioIdx, setAudioIdx] = useState(0);
  const [status, setStatus] = useState("loading");     // loading | playing | error
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(true);
  const [pos, setPos] = useState(0);
  const [bufEnd, setBufEnd] = useState(0);
  const [dur, setDur] = useState(0);
  const [vol, setVol] = useState(() => { const v = parseFloat(localStorage.getItem("kino_volume")); return isFinite(v) ? v : 1; });
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [full, setFull] = useState(false);
  const [showUi, setShowUi] = useState(true);
  const [menu, setMenu] = useState(null);              // "subs" | "audio" | "speed" | null
  const [uploadedSubs, setUploadedSubs] = useState([]);
  const [subSel, setSubSel] = useState(null);          // {type:"up",id} | {type:"emb",index} | null
  const [cues, setCues] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [nextCountdown, setNextCountdown] = useState(null);
  const [notice, setNotice] = useState("");

  const streamUrl = useCallback((m, start, aIdx) => {
    if (m === "direct") return `${API}/stream/${kind === "episode" ? "episode/" : ""}${id}?auth=${encodeURIComponent(token)}`;
    return `${API}/webplay/stream/${kind}/${id}?mode=${m}&start=${Math.max(0, start).toFixed(2)}&audio=${aIdx}&auth=${encodeURIComponent(token)}`;
  }, [kind, id, token]);

  const begin = useCallback((m, start = 0, aIdx = audioIdx) => {
    offsetRef.current = m === "direct" ? 0 : start;
    setMode(m);
    setStatus("loading");
    setBuffering(true);
    setSrc(streamUrl(m, start, aIdx));
    setPos(start);
  }, [audioIdx, streamUrl]);

  // --- load media info + saved position, then start in the best mode -----------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const progressPath = kind === "episode" ? `/progress/episode/${id}` : `/progress/${id}`;
        const subsPath = kind === "episode" ? `/episodes/${id}/subtitles` : `/movies/${id}/subtitles`;
        const [{ data: inf }, prog, subs] = await Promise.all([
          api.get(`/webplay/info/${kind}/${id}`),
          api.get(progressPath).then((r) => r.data).catch(() => null),
          api.get(subsPath).then((r) => r.data).catch(() => []),
        ]);
        if (cancelled) return;
        setInfo(inf);
        setDur(inf.duration || 0);
        setUploadedSubs(subs || []);
        const def = (subs || []).find((s) => s.is_default);
        if (def) setSubSel({ type: "up", id: def.id });
        const a = Math.max(0, (inf.audio || []).findIndex((x) => x.default));
        setAudioIdx(a);
        // Warm the server's subtitle cache in the background: extracting an embedded track reads the
        // whole file over the NAS (minutes for a big MKV), so start it now instead of when it's picked.
        const warm = (inf.subs || []).find((x) => x.text && /^(en|eng)$/i.test(x.lang || "")) || (inf.subs || []).find((x) => x.text);
        if (warm) fetch(`${API}/webplay/subtitle/${kind}/${id}/${warm.index}.vtt?auth=${encodeURIComponent(token)}`).catch(() => {});
        const d = decidePlayback(inf, caps, a);
        if (d.mode === "kodi") { onKodi && onKodi(d.reason); return; }
        let start = prog && prog.position_seconds > 30 && prog.duration_seconds && prog.position_seconds / prog.duration_seconds < 0.95 ? prog.position_seconds : 0;
        if (start > (inf.duration || 1e9) - 60) start = 0;
        offsetRef.current = d.mode === "direct" ? 0 : start;
        setMode(d.mode);
        setSrc(streamUrl(d.mode, start, a));
        setPos(start);
        if (d.mode === "direct" && start > 0) videoRef.current && (videoRef.current.dataset.resume = String(start));
      } catch (e) {
        if (!cancelled) { setStatus("error"); setError("Couldn't read this file's media info."); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  // --- video element events -----------------------------------------------------------------
  const virtualTime = useCallback(() => {
    const v = videoRef.current;
    return (v ? v.currentTime : 0) + offsetRef.current;
  }, []);

  const postProgress = useCallback((position, duration) => {
    if (!duration || position < 5) return;
    const body = kind === "episode"
      ? { episode_id: id, position_seconds: position, duration_seconds: duration }
      : { movie_id: id, position_seconds: position, duration_seconds: duration };
    api.post(kind === "episode" ? "/progress/episode" : "/progress", body).catch(() => {});
  }, [id, kind]);

  const saveProgress = useCallback((force = false) => {
    const v = videoRef.current;
    if (!v || !info) return;
    const now = Date.now();
    if (!force && now - lastSave.current < 5000) return;
    lastSave.current = now;
    postProgress(virtualTime(), info.duration || v.duration || 0);
  }, [info, postProgress, virtualTime]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const t = virtualTime();
    setPos(t);
    progRef.current = { pos: t, dur: (info && info.duration) || v.duration || 0 };
    const b = v.buffered;
    let end = v.currentTime;
    for (let i = 0; i < b.length; i++) if (b.start(i) <= v.currentTime + 0.5 && b.end(i) >= v.currentTime) end = b.end(i);
    setBufEnd(end + offsetRef.current);
    if (mode === "direct" && v.duration && isFinite(v.duration)) setDur(v.duration);
    saveProgress();
    if (next && dur > 0 && t > dur - 25 && nextCountdown === null) setNextCountdown(10);
  };

  const failOver = useCallback(async () => {
    // called when the current source errors: escalate direct -> remux -> transcode -> Kodi
    const v = info && info.video;
    let busy = false;
    if (mode !== "direct") {
      // cheap capacity check - re-requesting the stream itself would start (and hold) another ffmpeg
      try { busy = !(await api.get(`/webplay/status?mode=${mode}`)).data.free; } catch (e) { /* ignore */ }
    }
    if (busy) { setStatus("error"); setError("The server is busy with other streams right now."); return; }
    const t = virtualTime();
    if (mode === "direct" && v) {
      const d = decidePlayback({ ...info, container: "avi" }, caps, audioIdx); // force non-direct decision
      if (d.mode === "remux") { setNotice("Switched to compatibility mode"); begin("remux", t); return; }
      if (d.mode === "transcode") { setNotice("Converting video for your browser..."); begin("transcode", t); return; }
    }
    if (mode === "remux" && v && v.codec !== "hevc") { setNotice("Converting video for your browser..."); begin("transcode", t); return; }
    setStatus("error");
    setError("This file can't be played in the browser.");
  }, [audioIdx, begin, caps, info, mode, virtualTime]);

  // --- subtitles: fetch VTT, shift by nothing (cues are absolute; overlay uses virtual time) ---
  useEffect(() => {
    let cancelled = false;
    setCues([]);
    if (!subSel) return undefined;
    const url = subSel.type === "up"
      ? `${API}/subtitles/${subSel.id}/file?auth=${encodeURIComponent(token)}`
      : `${API}/webplay/subtitle/${kind}/${id}/${subSel.index}.vtt?auth=${encodeURIComponent(token)}`;
    setSubLoading(true);
    fetch(url).then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => { if (!cancelled) setCues(parseVtt(t)); })
      .catch(() => { if (!cancelled) setNotice("Couldn't load that subtitle track"); })
      .finally(() => { if (!cancelled) setSubLoading(false); });
    return () => { cancelled = true; };
  }, [subSel, kind, id, token]);

  const activeCue = useMemo(() => {
    if (!cues.length) return null;
    return cues.find((c) => pos >= c.start && pos <= c.end) || null;
  }, [cues, pos]);

  // --- controls -----------------------------------------------------------------------------
  const togglePlay = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  }, []);

  const seekTo = useCallback((t) => {
    const v = videoRef.current; if (!v || !info) return;
    const total = info.duration || v.duration || 0;
    t = Math.max(0, Math.min(t, total ? total - 1 : t));
    setNextCountdown(null);
    if (mode === "direct") { v.currentTime = t; setPos(t); return; }
    const rel = t - offsetRef.current;
    const b = v.buffered;
    for (let i = 0; i < b.length; i++) {
      if (rel >= b.start(i) && rel <= b.end(i) - 1) { v.currentTime = rel; setPos(t); return; }
    }
    begin(mode, t);   // outside buffered range: restart the stream at the target
  }, [begin, info, mode]);

  const changeAudio = (i) => {
    setAudioIdx(i); setMenu(null);
    const t = virtualTime();
    const d = decidePlayback(info, caps, i);
    if (d.mode === "kodi") { onKodi && onKodi(d.reason); return; }
    // a different audio track can't be selected in a direct-play file, so use a server stream
    const m = d.mode === "direct" ? "remux" : d.mode;
    begin(m, t, i);
  };

  const toggleFull = useCallback(() => {
    const el = wrapRef.current; if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen && el.requestFullscreen();
  }, []);

  useEffect(() => {
    const h = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const poke = useCallback(() => {
    setShowUi(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowUi(false), 3000);
  }, []);
  useEffect(() => { poke(); return () => clearTimeout(hideTimer.current); }, [poke]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      const v = videoRef.current; if (!v) return;
      const k = e.key;
      if (k === " " || k === "k") { e.preventDefault(); togglePlay(); }
      else if (k === "ArrowRight") { e.preventDefault(); seekTo(virtualTime() + 10); }
      else if (k === "ArrowLeft") { e.preventDefault(); seekTo(virtualTime() - 10); }
      else if (k === "ArrowUp") { e.preventDefault(); const n = Math.min(1, v.volume + 0.05); v.volume = n; setVol(n); }
      else if (k === "ArrowDown") { e.preventDefault(); const n = Math.max(0, v.volume - 0.05); v.volume = n; setVol(n); }
      else if (k === "m") { v.muted = !v.muted; setMuted(v.muted); }
      else if (k === "f") toggleFull();
      else if (k === "c") setSubSel((s) => (s ? null : (uploadedSubs[0] ? { type: "up", id: uploadedSubs[0].id } : ((info && info.subs || []).find((x) => x.text) ? { type: "emb", index: (info.subs.find((x) => x.text)).index } : null))));
      else if (k === "n" && next) nav(`/watch/episode/${next.id}`);
      poke();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [info, next, nav, poke, seekTo, toggleFull, togglePlay, uploadedSubs, virtualTime]);

  // volume / rate sync
  useEffect(() => { const v = videoRef.current; if (v) { v.volume = vol; v.muted = muted; localStorage.setItem("kino_volume", String(vol)); } }, [vol, muted, src]);
  useEffect(() => { const v = videoRef.current; if (v) v.playbackRate = rate; }, [rate, src]);

  // save progress when leaving
  // (React nulls videoRef before passive cleanups run, so use the last position captured in progRef)
  useEffect(() => () => { postProgress(progRef.current.pos, progRef.current.dur); }, [postProgress]);
  useEffect(() => {
    const h = () => saveProgress(true);
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [saveProgress]);

  // next-episode countdown
  useEffect(() => {
    if (nextCountdown === null || !next) return undefined;
    if (nextCountdown <= 0) { nav(`/watch/episode/${next.id}`); return undefined; }
    const t = setTimeout(() => setNextCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [nextCountdown, next, nav]);

  useEffect(() => { if (notice) { const t = setTimeout(() => setNotice(""), 4000); return () => clearTimeout(t); } return undefined; }, [notice]);

  // --- render -------------------------------------------------------------------------------
  const embSubs = (info && info.subs) || [];
  const audioTracks = (info && info.audio) || [];
  const pct = dur > 0 ? Math.min(100, (pos / dur) * 100) : 0;
  const bufPct = dur > 0 ? Math.min(100, (bufEnd / dur) * 100) : 0;
  const modeLabel = mode === "direct" ? "Direct play" : mode === "remux" ? "Compatibility mode" : mode === "transcode" ? "Converting live" : "";

  const btn = "p-2 text-white/80 hover:text-white transition-colors";
  const menuItem = (active) => `block w-full text-left px-4 py-2 text-xs hover:bg-white/10 ${active ? "text-[#D9381E]" : "text-white/80"}`;

  return (
    <div ref={wrapRef} className="fixed inset-0 bg-black z-50 select-none" onMouseMove={poke} onClick={() => setMenu(null)} data-testid="webplayer">
      <video
        ref={videoRef}
        src={src || undefined}
        autoPlay
        playsInline
        className="w-full h-full object-contain bg-black"
        onClick={(e) => { e.stopPropagation(); togglePlay(); }}
        onDoubleClick={toggleFull}
        onPlay={() => setPlaying(true)}
        onPause={() => { setPlaying(false); saveProgress(true); }}
        onWaiting={() => setBuffering(true)}
        onCanPlay={() => { setBuffering(false); setStatus("playing"); }}
        onPlaying={() => { setBuffering(false); setStatus("playing"); setPlaying(true); }}
        onLoadedMetadata={() => {
          const v = videoRef.current;
          if (v && v.dataset.resume) { v.currentTime = parseFloat(v.dataset.resume); delete v.dataset.resume; }
        }}
        onTimeUpdate={onTimeUpdate}
        onEnded={() => { saveProgress(true); if (next) nav(`/watch/episode/${next.id}`); }}
        onError={failOver}
        data-testid="webplayer-video"
      />

      {/* subtitle overlay */}
      {activeCue && (
        <div className={`absolute left-0 right-0 flex justify-center px-8 pointer-events-none transition-all ${showUi ? "bottom-28" : "bottom-10"}`}>
          <span className="max-w-3xl text-center text-white text-xl md:text-2xl leading-snug whitespace-pre-line px-3 py-1 bg-black/60 rounded"
            style={{ textShadow: "0 1px 3px #000" }} data-testid="webplayer-cue">{activeCue.text}</span>
        </div>
      )}

      {/* spinner */}
      {(status === "loading" || buffering) && status !== "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
          <div className="h-10 w-10 border-2 border-[#333] border-t-[#D9381E] rounded-full animate-spin" />
          {mode && mode !== "direct" && <p className="text-[#8A8A8A] text-xs uppercase tracking-[0.2em]">{mode === "transcode" ? "Converting for your browser..." : "Preparing..."}</p>}
        </div>
      )}

      {notice && <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-black/80 border border-[#333] text-white text-xs px-4 py-2">{notice}</div>}

      {/* error / fallback */}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/90 px-6 text-center" data-testid="webplayer-error">
          <MonitorPlay size={40} strokeWidth={1.5} className="text-[#fca5a5]" />
          <p className="text-white">{error || "Playback failed."}</p>
          <div className="flex gap-2">
            {info && <button onClick={() => { setStatus("loading"); const d = decidePlayback(info, caps, audioIdx); begin(d.mode === "kodi" ? "transcode" : d.mode, virtualTime()); }}
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2"><RotateCw size={13} strokeWidth={1.5} /> Try again</button>}
            {onKodi && <button onClick={() => onKodi(error)} className="bg-[#D9381E] hover:bg-[#ED4B32] text-white px-6 py-2 text-xs uppercase tracking-[0.2em]" data-testid="webplayer-open-kodi">Open in Kodi</button>}
          </div>
        </div>
      )}

      {/* next episode card */}
      {next && nextCountdown !== null && (
        <div className="absolute right-6 bottom-32 bg-black/85 border border-[#333] p-4 w-72" onClick={(e) => e.stopPropagation()}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">Next episode in {nextCountdown}s</p>
          <p className="text-white text-sm mt-1 truncate">{next.title}</p>
          <div className="flex gap-2 mt-3">
            <button onClick={() => nav(`/watch/episode/${next.id}`)} className="flex-1 bg-[#D9381E] hover:bg-[#ED4B32] text-white text-xs uppercase tracking-[0.2em] py-2">Play now</button>
            <button onClick={() => setNextCountdown(null)} className="flex-1 border border-[#333] text-white/80 hover:text-white text-xs uppercase tracking-[0.2em] py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* top bar */}
      <div className={`absolute top-0 left-0 right-0 p-5 flex items-start justify-between bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${showUi || !playing ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <button onClick={(e) => { e.stopPropagation(); saveProgress(true); nav(-1); }} className="flex items-center gap-2 text-white/80 hover:text-white bg-black/60 hover:bg-black px-4 py-2 transition-colors" data-testid="webplayer-back">
          <ArrowLeft size={16} strokeWidth={1.5} /><span className="text-xs uppercase tracking-[0.2em]">Back</span>
        </button>
        <div className="text-right">
          <h2 className="font-display text-xl text-white tracking-tight">{title}</h2>
          {subtitle && <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">{subtitle}</p>}
          <div className="mt-2 flex items-center justify-end gap-3">
            {modeLabel && <span className={`text-[10px] uppercase tracking-[0.2em] ${mode === "direct" ? "text-[#86efac]" : "text-[#fcd34d]"}`}>{modeLabel}</span>}
            {onKodi && <button onClick={(e) => { e.stopPropagation(); onKodi(""); }} className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#333] hover:border-white px-3 py-1" data-testid="webplayer-kodi">Open in Kodi</button>}
          </div>
        </div>
      </div>

      {/* bottom controls */}
      <div className={`absolute bottom-0 left-0 right-0 px-5 pb-4 pt-10 bg-gradient-to-t from-black/90 to-transparent transition-opacity duration-300 ${showUi || !playing ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={(e) => e.stopPropagation()}>
        {/* seek bar */}
        <div className="group relative h-4 flex items-center cursor-pointer"
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seekTo(((e.clientX - r.left) / r.width) * dur); }}
          data-testid="webplayer-seek">
          <div className="relative w-full h-1 group-hover:h-1.5 bg-white/20 transition-all">
            <div className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${bufPct}%` }} />
            <div className="absolute inset-y-0 left-0 bg-[#D9381E]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1">
            <button className={btn} onClick={togglePlay} data-testid="webplayer-playpause">{playing ? <Pause size={20} strokeWidth={1.5} /> : <Play size={20} strokeWidth={1.5} />}</button>
            <button className={btn} onClick={() => seekTo(virtualTime() - 10)} title="Back 10s"><SkipBack size={18} strokeWidth={1.5} /></button>
            <button className={btn} onClick={() => seekTo(virtualTime() + 10)} title="Forward 10s"><SkipForward size={18} strokeWidth={1.5} /></button>
            <button className={btn} onClick={() => { setMuted(!muted); }} title="Mute">{muted || vol === 0 ? <VolumeX size={18} strokeWidth={1.5} /> : <Volume2 size={18} strokeWidth={1.5} />}</button>
            <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : vol} onChange={(e) => { const n = parseFloat(e.target.value); setVol(n); setMuted(n === 0); }} className="w-24 accent-[#D9381E]" aria-label="Volume" />
            <span className="ml-3 text-xs text-white/80 tabular-nums">{fmtTime(pos)} / {fmtTime(dur)}</span>
          </div>
          <div className="flex items-center gap-1 relative">
            {next && <button className={btn} onClick={() => nav(`/watch/episode/${next.id}`)} title="Next episode (n)"><SkipForward size={18} strokeWidth={1.5} /></button>}
            {audioTracks.length > 1 && (
              <div className="relative">
                <button className={btn} onClick={() => setMenu(menu === "audio" ? null : "audio")} title="Audio track"><AudioLines size={18} strokeWidth={1.5} /></button>
                {menu === "audio" && (
                  <div className="absolute bottom-full right-0 mb-2 w-64 max-h-64 overflow-auto bg-black/95 border border-[#333]">
                    {audioTracks.map((a) => (
                      <button key={a.index} className={menuItem(a.index === audioIdx)} onClick={() => changeAudio(a.index)}>
                        {(a.lang || "und").toUpperCase()} · {a.codec.toUpperCase()}{a.channels ? ` ${a.channels}ch` : ""}{a.title ? ` · ${a.title}` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(uploadedSubs.length > 0 || embSubs.some((s) => s.text)) && (
              <div className="relative">
                <button className={`${btn} ${subSel ? "!text-[#D9381E]" : ""}`} onClick={() => setMenu(menu === "subs" ? null : "subs")} title="Subtitles (c)" data-testid="webplayer-subs"><Subtitles size={18} strokeWidth={1.5} /></button>
                {menu === "subs" && (
                  <div className="absolute bottom-full right-0 mb-2 w-64 max-h-64 overflow-auto bg-black/95 border border-[#333]">
                    <button className={menuItem(!subSel)} onClick={() => { setSubSel(null); setMenu(null); }}>Off</button>
                    {uploadedSubs.map((s) => (
                      <button key={s.id} className={menuItem(subSel && subSel.type === "up" && subSel.id === s.id)} onClick={() => { setSubSel({ type: "up", id: s.id }); setMenu(null); }}>{s.label || s.language}</button>
                    ))}
                    {embSubs.filter((s) => s.text).map((s) => (
                      <button key={`e${s.index}`} className={menuItem(subSel && subSel.type === "emb" && subSel.index === s.index)} onClick={() => { setSubSel({ type: "emb", index: s.index }); setMenu(null); }}>
                        {(s.lang || "und").toUpperCase()}{s.title ? ` · ${s.title}` : ""} (embedded)
                      </button>
                    ))}
                    {subLoading && <p className="px-4 py-2 text-[10px] text-[#8A8A8A]">Loading subtitles...</p>}
                  </div>
                )}
              </div>
            )}
            <div className="relative">
              <button className={btn} onClick={() => setMenu(menu === "speed" ? null : "speed")} title="Speed"><Gauge size={18} strokeWidth={1.5} /></button>
              {menu === "speed" && (
                <div className="absolute bottom-full right-0 mb-2 w-28 bg-black/95 border border-[#333]">
                  {SPEEDS.map((s) => <button key={s} className={menuItem(s === rate)} onClick={() => { setRate(s); setMenu(null); }}>{s}x</button>)}
                </div>
              )}
            </div>
            {document.pictureInPictureEnabled && <button className={btn} onClick={() => { const v = videoRef.current; if (!v) return; if (document.pictureInPictureElement) document.exitPictureInPicture(); else v.requestPictureInPicture && v.requestPictureInPicture().catch(() => {}); }} title="Picture in picture"><PictureInPicture2 size={18} strokeWidth={1.5} /></button>}
            <button className={btn} onClick={toggleFull} title="Fullscreen (f)" data-testid="webplayer-fullscreen">{full ? <Minimize size={18} strokeWidth={1.5} /> : <Maximize size={18} strokeWidth={1.5} />}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
